import { saveToMemory } from "@utils/storage.utils";
import { ArrowRight } from "lucide-react";
import React, { useState } from "react";
import { NavLink } from "react-router-dom";
import { ILesson } from "src/types";
import EditLessonModal from "./editLessonModel";

interface LessonCardProps {
  lesson: ILesson;
}

const LessonCard: React.FC<LessonCardProps> = ({ lesson }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [editingLevel, setEditingLesson] = useState<ILesson | null>(null);

  const redirectActivitiesPage = (lesson: ILesson) => {
    saveToMemory("lessonId", lesson.id);
    saveToMemory(
      "CurrentLessonInfo",
      JSON.stringify({
        title: lesson.title,
        lessonNumber: lesson.lessonNumber,
        lessonId: lesson.id,
      })
    );
    return;
  };

  return (
    <div className="bg-white rounded-xl shadow-lg mb-4 p-6 w-full flex flex-col space-y-4">
      <div className="flex justify-end">
        <div className="rounded-full bg-brand w-8 h-8 flex items-center justify-center text-white font-bold">
          {lesson?.lessonNumber ?? "—"}
        </div>
      </div>

      <div
        className="flex justify-between items-center cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <h2 className="text-xl font-semibold text-gray-900">{lesson.title}</h2>
        <ArrowRight
          className={`transition-transform duration-300 ${
            isExpanded ? "rotate-90" : ""
          }`}
        />
      </div>

      <p className="text-sm text-gray-600 line-clamp-2">{lesson.description}</p>

      {isExpanded && (
        <div className="mt-2 p-5 bg-gray-50 rounded-lg shadow-inner space-y-4">
          <div className="grid grid-cols-2 gap-4 text-sm text-gray-700">
            <p>
              <strong>Lesson Number:</strong> {lesson.lessonNumber}
            </p>
            <p>
              <strong>Exercises:</strong> {lesson.exerciseCount}
            </p>
          </div>

          <div>
            <h3 className="text-md font-semibold text-gray-800 mb-1">
              Lesson Goal
            </h3>
            <p className="text-sm text-gray-600">{lesson.goal}</p>
          </div>

          <div>
            <h3 className="text-md font-semibold text-gray-800 mb-1">
              Key Takeaways
            </h3>
            <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
              {lesson.points.map((point, index) => (
                <li key={index}>{point}</li>
              ))}
            </ul>
          </div>

          <div className="flex flex-wrap justify-between items-center gap-3 pt-2">
            <div className="flex flex-wrap gap-3">
              <NavLink
                to={`/dashboard/levels/lessons/activities`}
                className="px-5 py-2 text-sm rounded-lg bg-brand text-white hover:bg-brand-dark transition"
                onClick={() => redirectActivitiesPage(lesson)}
              >
                Go to Activities
              </NavLink>

              <NavLink
                to={`/dashboard/levels/lessons/chats/series/list`}
                className="px-5 py-2 text-sm rounded-lg bg-brand text-white hover:bg-brand-dark transition"
                onClick={() => redirectActivitiesPage(lesson)}
              >
                View Chat Series
              </NavLink>
            </div>

            <button
              onClick={() =>
                (window.location.href = `/dashboard/levels/lessons/add/${lesson.id}`)
              }
              type="button"
              className="px-5 py-2 rounded-md border border-brand text-brand hover:bg-brand hover:text-white transition text-sm"
            >
              Edit Lesson
            </button>
          </div>

          {editingLevel && (
            <EditLessonModal
              onClose={() => setEditingLesson(null)}
              lesson={editingLevel}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default LessonCard;
